/**
 * ClientAccount — BM-H4699 (24 Oct 2026).
 *
 * The client account records the SRA Accounts Rules 2019 ask for, on one
 * page, for Accounts and the directors:
 *
 *   Ledgers        rule 8.1(b): every matter's client ledger balance as at a
 *                  date, with the total (GET /money-on-account/ledger-listing)
 *   Cash book      rule 8.1(a): one client bank account's movements with a
 *                  running balance, and the correction button
 *   Bank accounts  the register (Lloyds now, Allica soon); directors add,
 *                  make default and close; older rows are assigned here
 *   Opening        LawBillity opening balances: preview, fix, then load once
 *
 * Every sum is done by the server in whole pence. This page only shows it.
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Landmark, BookOpen, ListOrdered, Upload, RefreshCw, AlertCircle,
  Star, Lock, Plus, Check, X,
} from "lucide-react";
import { API } from "../config/api";
import { getUserRole, ROLES } from "../config/roleConfig";
import ReverseControl from "../components/ReverseLedgerEntry";

const EM_DASH = "—";
const TABS = [
  { key: "ledgers", label: "Ledgers", Icon: ListOrdered },
  { key: "cashbook", label: "Cash book", Icon: BookOpen },
  { key: "accounts", label: "Bank accounts", Icon: Landmark },
  { key: "opening", label: "Opening balances", Icon: Upload },
];

function _email() {
  return sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "";
}

function _headers() {
  const email = _email();
  return {
    "X-Bonsai-User-Id": sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || email,
    "X-User-Email": email,
  };
}

function _today() {
  return new Date().toISOString().slice(0, 10);
}

export function fmtPence(p) {
  if (p === null || p === undefined || p === "") return EM_DASH;
  const n = Number(p);
  if (!Number.isFinite(n)) return EM_DASH;
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const pounds = Math.floor(abs / 100).toLocaleString("en-GB");
  return `${sign}£${pounds}.${String(abs % 100).padStart(2, "0")}`;
}

function _err(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fallback;
}

function _fmtSort(s) {
  return s && s.length === 6 ? `${s.slice(0, 2)}-${s.slice(2, 4)}-${s.slice(4)}` : s || EM_DASH;
}

const inputCls = "px-2 py-1 rounded bg-navy-hover border border-navy-border text-white text-xs focus:outline-none focus:border-indigo-400";
const btnCls = "inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs border border-indigo-400/40 bg-indigo-500/15 text-indigo-100 hover:bg-indigo-500/30 disabled:opacity-40";

function ErrorBand({ text, testId }) {
  if (!text) return null;
  return (
    <div className="flex items-center gap-2 bg-status-red-dim/20 border border-status-red/30 rounded-lg px-3 py-2 my-2" data-testid={testId}>
      <AlertCircle className="w-4 h-4 text-status-red shrink-0" />
      <span className="text-xs text-status-red">{text}</span>
    </div>
  );
}

// ── Ledgers ──────────────────────────────────────────────────────────────────

function LedgersTab() {
  const [asAt, setAsAt] = useState(_today());
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await axios.get(`${API}/money-on-account/ledger-listing`, { params: { as_at: asAt }, headers: _headers() });
      setData(res.data);
    } catch (e) {
      setError(_err(e, "Couldn't load the client ledgers"));
    }
  }, [asAt]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="client-account-ledgers">
      <div className="flex items-center gap-2 mb-3">
        <label className="text-[11px] text-txt-muted">As at</label>
        <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} className={inputCls} data-testid="ledgers-as-at" />
        <button onClick={load} className="text-gray-400 hover:text-white" aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      <ErrorBand text={error} testId="ledgers-error" />
      {data && (
        <>
          <div className="flex flex-wrap gap-4 text-xs mb-3">
            <span className="text-txt-muted">Total held <span className="text-white font-semibold tabular-nums" data-testid="ledgers-total">{fmtPence(data.total_pence)}</span></span>
            <span className="text-txt-muted">Matters with money <span className="text-white">{data.matters.length}</span></span>
            {data.overdrawn_count > 0 && (
              <span className="text-status-red font-semibold" data-testid="ledgers-overdrawn">{data.overdrawn_count} overdrawn</span>
            )}
          </div>
          {data.matters.length === 0 ? (
            <p className="text-xs text-gray-500 py-6 text-center">No client money held at this date</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="text-left font-semibold px-2 py-1.5">Matter</th>
                  <th className="text-right font-semibold px-2 py-1.5">Balance</th>
                  <th className="text-right font-semibold px-2 py-1.5">Running total</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {data.matters.map((m) => (
                  <tr key={m.matter_reference} className="border-t border-white/5" data-testid="ledgers-row">
                    <td className="px-2 py-1.5 font-mono">
                      <Link to={`/matter/${encodeURIComponent(m.matter_reference)}`} className="text-indigo-200 hover:underline">{m.matter_reference}</Link>
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${m.overdrawn ? "text-status-red font-semibold" : "text-white"}`}>{fmtPence(m.balance_pence)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{fmtPence(m.running_total_pence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

// ── Cash book ────────────────────────────────────────────────────────────────

function CashBookTab({ accounts }) {
  const [accountId, setAccountId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [book, setBook] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!accountId && accounts.length) {
      setAccountId((accounts.find((a) => a.is_default) || accounts[0]).id);
    }
  }, [accounts, accountId]);

  const load = useCallback(async () => {
    if (!accountId) return;
    setError("");
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await axios.get(`${API}/money-on-account/bank-accounts/${accountId}/cash-book`, { params, headers: _headers() });
      setBook(res.data);
    } catch (e) {
      setError(_err(e, "Couldn't load the cash book"));
    }
  }, [accountId, from, to]);
  useEffect(() => { load(); }, [load]);

  if (!accounts.length) {
    return <p className="text-xs text-gray-500 py-6 text-center" data-testid="cashbook-no-accounts">Register the client bank account first, on Bank accounts.</p>;
  }
  return (
    <div data-testid="client-account-cashbook">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputCls} data-testid="cashbook-account">
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.status === "closed" ? " (closed)" : ""}</option>)}
        </select>
        <label className="text-[11px] text-txt-muted">From</label>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
        <label className="text-[11px] text-txt-muted">To</label>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
      </div>
      <ErrorBand text={error} testId="cashbook-error" />
      {book && (
        <>
          <div className="flex flex-wrap gap-4 text-xs mb-3">
            <span className="text-txt-muted">Brought forward <span className="text-white tabular-nums">{fmtPence(book.opening_balance_pence)}</span></span>
            <span className="text-txt-muted">Receipts <span className="text-white tabular-nums">{fmtPence(book.receipts_pence)}</span></span>
            <span className="text-txt-muted">Payments <span className="text-white tabular-nums">{fmtPence(book.payments_pence)}</span></span>
            <span className="text-txt-muted">Balance <span className="text-white font-semibold tabular-nums" data-testid="cashbook-closing">{fmtPence(book.closing_balance_pence)}</span></span>
          </div>
          {book.lines.length === 0 ? (
            <p className="text-xs text-gray-500 py-6 text-center">No movements in this period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                    <th className="text-left font-semibold px-2 py-1.5">Date</th>
                    <th className="text-left font-semibold px-2 py-1.5">Matter</th>
                    <th className="text-left font-semibold px-2 py-1.5">Details</th>
                    <th className="text-right font-semibold px-2 py-1.5">Receipts</th>
                    <th className="text-right font-semibold px-2 py-1.5">Payments</th>
                    <th className="text-right font-semibold px-2 py-1.5">Balance</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  {book.lines.map((l) => (
                    <tr key={l.entry_id} className={`border-t border-white/5 ${l.reversed_by_entry_id ? "opacity-50" : ""}`} data-testid="cashbook-line">
                      <td className="px-2 py-1.5 whitespace-nowrap">{l.date}</td>
                      <td className="px-2 py-1.5 font-mono">{l.matter_reference}</td>
                      <td className="px-2 py-1.5 truncate max-w-[260px]" title={l.description}>{l.description}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{l.receipt_pence ? fmtPence(l.receipt_pence) : ""}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{l.payment_pence ? fmtPence(l.payment_pence) : ""}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${l.balance_pence < 0 ? "text-status-red" : "text-slate-400"}`}>{fmtPence(l.balance_pence)}</td>
                      <td className="px-2 py-1.5 text-right">
                        {!l.reverses_entry_id && !l.reversed_by_entry_id && (
                          <ReverseControl entryId={l.entry_id} onDone={load} testPrefix="cashbook-reverse" />
                        )}
                        {l.reverses_entry_id && <span className="text-[10px] text-slate-500">correction</span>}
                        {l.reversed_by_entry_id && <span className="text-[10px] text-slate-500">reversed</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Bank accounts ────────────────────────────────────────────────────────────

const EMPTY_ACCOUNT = { name: "", bank: "", sort_code: "", account_number_last4: "", opened_on: "" };

function AccountsTab({ accounts, reload, isDirector }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [closing, setClosing] = useState({});
  const [unassigned, setUnassigned] = useState(null);
  const [picked, setPicked] = useState({});
  const [assignTo, setAssignTo] = useState("");

  const loadUnassigned = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/money-on-account/bank-accounts/unassigned`, { headers: _headers() });
      setUnassigned(res.data);
    } catch (e) {
      setError(_err(e, "Couldn't load the rows with no bank account"));
    }
  }, []);
  useEffect(() => { loadUnassigned(); }, [loadUnassigned]);

  const act = async (fn, fallback) => {
    setError("");
    try {
      await fn();
      await reload();
      await loadUnassigned();
      return true;
    } catch (e) {
      setError(_err(e, fallback));
      return false;
    }
  };

  const create = async () => {
    const ok = await act(() => axios.post(`${API}/money-on-account/bank-accounts`, form, { headers: _headers() }), "Couldn't register the account");
    if (ok) setForm(null);
  };

  const open = accounts.filter((a) => a.status === "open");
  const pickedIds = Object.keys(picked).filter((k) => picked[k]);

  return (
    <div data-testid="client-account-accounts">
      <ErrorBand text={error} testId="accounts-error" />
      {accounts.length === 0 && <p className="text-xs text-gray-500 py-3" data-testid="accounts-empty">No client bank account registered yet.</p>}
      {accounts.length > 0 && (
        <table className="w-full text-xs mb-3">
          <thead>
            <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="text-left font-semibold px-2 py-1.5">Account</th>
              <th className="text-left font-semibold px-2 py-1.5">Sort code</th>
              <th className="text-left font-semibold px-2 py-1.5">Number</th>
              <th className="text-left font-semibold px-2 py-1.5">Opened</th>
              <th className="text-left font-semibold px-2 py-1.5">Status</th>
              <th className="px-2 py-1.5" />
            </tr>
          </thead>
          <tbody className="text-slate-300">
            {accounts.map((a) => (
              <tr key={a.id} className="border-t border-white/5" data-testid="accounts-row">
                <td className="px-2 py-1.5 text-white">
                  {a.is_default && <Star className="inline w-3 h-3 text-indigo-300 mr-1" aria-label="Default" />}
                  {a.name}
                </td>
                <td className="px-2 py-1.5 font-mono">{_fmtSort(a.sort_code)}</td>
                <td className="px-2 py-1.5 font-mono">{"••••"}{a.account_number_last4}</td>
                <td className="px-2 py-1.5">{a.opened_on || EM_DASH}</td>
                <td className="px-2 py-1.5">
                  {a.status === "open"
                    ? <span className="px-1.5 py-0.5 rounded-full border text-[10px] bg-teal/15 text-teal border-teal/30">Open</span>
                    : <span className="px-1.5 py-0.5 rounded-full border text-[10px] bg-white/5 text-slate-400 border-white/10">Closed {a.closed_on}</span>}
                </td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {isDirector && a.status === "open" && !a.is_default && (
                    <button onClick={() => act(() => axios.post(`${API}/money-on-account/bank-accounts/${a.id}/default`, {}, { headers: _headers() }), "Couldn't change the default")} className="text-gray-400 hover:text-white mr-2" title="Make default" aria-label="Make default">
                      <Star className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isDirector && a.status === "open" && (
                    closing[a.id] !== undefined ? (
                      <span className="inline-flex items-center gap-1">
                        <input type="date" value={closing[a.id]} onChange={(e) => setClosing({ ...closing, [a.id]: e.target.value })} className={inputCls} />
                        <button onClick={async () => { if (await act(() => axios.post(`${API}/money-on-account/bank-accounts/${a.id}/close`, { closed_on: closing[a.id] }, { headers: _headers() }), "Couldn't close the account")) setClosing({}); }} className="text-indigo-200 hover:text-white" aria-label="Close account"><Check className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setClosing({})} className="text-gray-400 hover:text-white" aria-label="Cancel"><X className="w-3.5 h-3.5" /></button>
                      </span>
                    ) : (
                      <button onClick={() => setClosing({ [a.id]: _today() })} className="text-gray-400 hover:text-white" title="Close account (cash book must be on nil)" aria-label="Close">
                        <Lock className="w-3.5 h-3.5" />
                      </button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {isDirector && !form && (
        <button onClick={() => setForm({ ...EMPTY_ACCOUNT })} className={btnCls} data-testid="accounts-add"><Plus className="w-3 h-3" />Add account</button>
      )}
      {isDirector && form && (
        <div className="flex flex-wrap items-end gap-2 p-3 rounded-lg border border-white/10 bg-white/5" data-testid="accounts-form">
          {[["name", "Name", "Lloyds client account"], ["bank", "Bank", "Lloyds"], ["sort_code", "Sort code", "30-00-00"], ["account_number_last4", "Last 4 digits", "1234"]].map(([k, label, ph]) => (
            <label key={k} className="flex flex-col text-[10px] text-txt-muted gap-0.5">
              {label}
              <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className={inputCls} data-testid={`accounts-form-${k}`} />
            </label>
          ))}
          <label className="flex flex-col text-[10px] text-txt-muted gap-0.5">
            Opened
            <input type="date" value={form.opened_on} onChange={(e) => setForm({ ...form, opened_on: e.target.value })} className={inputCls} data-testid="accounts-form-opened_on" />
          </label>
          <button onClick={create} className={btnCls} data-testid="accounts-form-save"><Check className="w-3 h-3" />Save</button>
          <button onClick={() => setForm(null)} className="text-gray-400 hover:text-white text-xs">Cancel</button>
        </div>
      )}

      {unassigned && unassigned.count > 0 && (
        <div className="mt-5" data-testid="accounts-unassigned">
          <h4 className="text-xs font-semibold text-white mb-1">Entries that do not say which bank account they went through ({unassigned.count}, {fmtPence(unassigned.total_pence)})</h4>
          <p className="text-[11px] text-txt-muted mb-2">Entered before the register existed. Tick each one and say which account it went through. Each is assigned once.</p>
          <table className="w-full text-xs mb-2">
            <tbody className="text-slate-300">
              {unassigned.rows.map((r) => (
                <tr key={r.entry_id} className="border-t border-white/5">
                  <td className="px-2 py-1"><input type="checkbox" checked={!!picked[r.entry_id]} onChange={(e) => setPicked({ ...picked, [r.entry_id]: e.target.checked })} aria-label={`Pick ${r.entry_id}`} /></td>
                  <td className="px-2 py-1">{r.date}</td>
                  <td className="px-2 py-1 font-mono">{r.matter_reference}</td>
                  <td className="px-2 py-1 truncate max-w-[240px]">{r.description}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmtPence(r.amount_pence)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {open.length > 0 && (
            <div className="flex items-center gap-2">
              <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className={inputCls}>
                <option value="">Choose account</option>
                {open.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <button disabled={!assignTo || pickedIds.length === 0} onClick={async () => { if (await act(() => axios.post(`${API}/money-on-account/bank-accounts/${assignTo}/assign`, { entry_ids: pickedIds }, { headers: _headers() }), "Couldn't assign the entries")) setPicked({}); }} className={btnCls}>
                Assign {pickedIds.length || ""}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Opening balances ─────────────────────────────────────────────────────────

const STATUS_CHIP = {
  matter: ["Matter", "bg-teal/15 text-teal border-teal/30"],
  mapped: ["Mapped", "bg-teal/15 text-teal border-teal/30"],
  reference_only: ["Not in CaseMinder", "bg-status-amber-dim/20 text-status-amber border-status-amber/30"],
  unmatched: ["Fix", "bg-status-red-dim/20 text-status-red border-status-red/30"],
  nil: ["Nil", "bg-white/5 text-slate-400 border-white/10"],
};

function OpeningTab({ accounts }) {
  const [lock, setLock] = useState(null);
  const [file, setFile] = useState(null);
  const [cutOff, setCutOff] = useState("");
  const [accountId, setAccountId] = useState("");
  const [maps, setMaps] = useState({});
  const [preview, setPreview] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const loadLock = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/money-on-account/import-lock-status`, { headers: _headers() });
      setLock(res.data);
    } catch {
      setLock(null);
    }
  }, []);
  useEffect(() => { loadLock(); }, [loadLock]);
  useEffect(() => {
    if (!accountId) {
      const d = accounts.find((a) => a.is_default && a.status === "open");
      if (d) setAccountId(d.id);
    }
  }, [accounts, accountId]);

  const form = (extra = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("cut_off", cutOff);
    fd.append("bank_account_id", accountId);
    const m = Object.fromEntries(Object.entries(maps).filter(([, v]) => v && v.trim()));
    fd.append("mappings", JSON.stringify(m));
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return fd;
  };

  const runPreview = async () => {
    setBusy(true);
    setError("");
    setAgreed(false);
    try {
      const res = await axios.post(`${API}/money-on-account/opening-balances/preview`, form(), { headers: _headers() });
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
      setError(_err(e, "Couldn't read the file"));
    } finally {
      setBusy(false);
    }
  };

  const runCommit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await axios.post(`${API}/money-on-account/opening-balances/commit`, form({
        expected_total_pence: String(preview.total_pence),
        file_sha256: preview.file_sha256,
      }), { headers: _headers() });
      setDone(res.data);
      setPreview(null);
      loadLock();
    } catch (e) {
      setError(_err(e, "Couldn't load the balances"));
    } finally {
      setBusy(false);
    }
  };

  const openAccounts = accounts.filter((a) => a.status === "open");

  if (done) {
    return (
      <div className="p-3 rounded-lg border border-teal/30 bg-teal/10 text-xs" data-testid="opening-done">
        Loaded {done.rows_written} opening balances at {done.cut_off} into {done.bank_account?.name}, totalling {fmtPence(done.total_pence)}
        {done.overdrawn ? `, ${done.overdrawn} overdrawn` : ""}.
      </div>
    );
  }
  return (
    <div data-testid="client-account-opening">
      {lock?.locked && (
        <div className="p-3 rounded-lg border border-white/10 bg-white/5 text-xs mb-3 flex items-center gap-2" data-testid="opening-locked">
          <Lock className="w-3.5 h-3.5 text-indigo-300" />
          Opening balances were loaded{lock.completed_at ? ` on ${new Date(lock.completed_at).toLocaleDateString("en-GB")}` : ""}{lock.completed_by ? ` by ${lock.completed_by}` : ""}.
        </div>
      )}
      {!lock?.locked && (
        <>
          <p className="text-[11px] text-txt-muted mb-3">
            Export LawBillity's trust account report to the cut-off date, choose it here and preview. Nothing is written until you load it, and it can be loaded once.
          </p>
          {openAccounts.length === 0 && <ErrorBand text="Register the client bank account first, on Bank accounts." testId="opening-no-account" />}
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <label className="flex flex-col text-[10px] text-txt-muted gap-0.5">
              Cut-off date
              <input type="date" value={cutOff} onChange={(e) => { setCutOff(e.target.value); setPreview(null); }} className={inputCls} data-testid="opening-cut-off" />
            </label>
            <label className="flex flex-col text-[10px] text-txt-muted gap-0.5">
              Held in
              <select value={accountId} onChange={(e) => { setAccountId(e.target.value); setPreview(null); }} className={inputCls} data-testid="opening-account">
                <option value="">Choose account</option>
                {openAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
            <label className="flex flex-col text-[10px] text-txt-muted gap-0.5">
              LawBillity export (CSV)
              <input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} className="text-xs text-slate-300" data-testid="opening-file" />
            </label>
            <button onClick={runPreview} disabled={busy || !file || !cutOff || !accountId} className={btnCls} data-testid="opening-preview">
              {preview ? "Preview again" : "Preview"}
            </button>
          </div>
        </>
      )}
      <ErrorBand text={error} testId="opening-error" />
      {preview && (
        <div data-testid="opening-preview-result">
          <div className="flex flex-wrap gap-4 text-xs mb-2">
            <span className="text-txt-muted">Accounts <span className="text-white">{preview.accounts_found}</span></span>
            <span className="text-txt-muted">To load <span className="text-white">{preview.counts.to_write}</span></span>
            <span className="text-txt-muted">Total <span className="text-white font-semibold tabular-nums" data-testid="opening-total">{fmtPence(preview.total_pence)}</span></span>
            {preview.counts.overdrawn > 0 && <span className="text-status-red font-semibold">{preview.counts.overdrawn} overdrawn ({fmtPence(preview.overdrawn_pence)})</span>}
          </div>
          {preview.blocking.length > 0 && (
            <ul className="mb-2 text-[11px] text-status-red list-disc pl-5" data-testid="opening-blocking">
              {preview.blocking.map((b) => <li key={b}>{b}</li>)}
            </ul>
          )}
          <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="text-left font-semibold px-2 py-1.5">LawBillity account</th>
                  <th className="text-left font-semibold px-2 py-1.5">Matter</th>
                  <th className="text-right font-semibold px-2 py-1.5">Balance</th>
                  <th className="text-left font-semibold px-2 py-1.5" />
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {preview.accounts.map((l) => {
                  const [label, cls] = STATUS_CHIP[l.status] || [l.status, ""];
                  return (
                    <tr key={l.lawbillity_name} className="border-t border-white/5" data-testid="opening-line">
                      <td className="px-2 py-1.5 font-mono">{l.lawbillity_name}</td>
                      <td className="px-2 py-1.5 font-mono">
                        {l.status === "unmatched" || l.status === "mapped" ? (
                          <input value={maps[l.lawbillity_name] ?? (l.matter_reference || "")} onChange={(e) => setMaps({ ...maps, [l.lawbillity_name]: e.target.value })} placeholder="Matter reference" className={`${inputCls} w-40`} />
                        ) : (l.matter_reference || EM_DASH)}
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${l.overdrawn ? "text-status-red font-semibold" : ""}`}>{fmtPence(l.balance_pence)}</td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded-full border text-[10px] ${cls}`} title={l.note}>{label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {preview.can_commit && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-slate-300">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="opening-agree" />
                {fmtPence(preview.total_pence)} matches LawBillity's client ledger total at {preview.cut_off}
              </label>
              <button onClick={runCommit} disabled={!agreed || busy} className={btnCls} data-testid="opening-commit">Load {preview.counts.to_write} balances</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function ClientAccount() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "ledgers";
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState("");
  const isDirector = getUserRole(_email()) === ROLES.DIRECTOR;

  const loadAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/money-on-account/bank-accounts`, { headers: _headers() });
      setAccounts(Array.isArray(res.data?.accounts) ? res.data.accounts : []);
      setError("");
    } catch (e) {
      setError(_err(e, "Couldn't load the client bank accounts"));
    }
  }, []);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  return (
    <div className="p-6 max-w-6xl" data-testid="client-account-page">
      <h1 className="text-lg font-bold text-white flex items-center gap-2 mb-1">
        <Landmark className="w-5 h-5 text-indigo-300" />
        Client Account
      </h1>
      <p className="text-xs text-txt-muted mb-4">Client ledgers, cash book and bank accounts (SRA Accounts Rules 8.1 and 8.3).</p>
      <div className="flex gap-1 mb-4 border-b border-white/10">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 -mb-px ${tab === key ? "border-indigo-400 text-white" : "border-transparent text-txt-muted hover:text-white"}`}
            data-testid={`client-account-tab-${key}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>
      <ErrorBand text={error} testId="client-account-error" />
      <section className="bg-navy-card border border-white/10 rounded-xl p-4">
        {tab === "ledgers" && <LedgersTab />}
        {tab === "cashbook" && <CashBookTab accounts={accounts} />}
        {tab === "accounts" && <AccountsTab accounts={accounts} reload={loadAccounts} isDirector={isDirector} />}
        {tab === "opening" && <OpeningTab accounts={accounts} />}
      </section>
    </div>
  );
}
